import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {dispatchStatus, DispatchStatus, toDispatchStatus} from "./dispatch-status";
import {Entity} from "./entity";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {toTransportCategory, TransportCategory} from "./transport-category";
import {DispatchRequest, DispatchRequestEntity, DispatchRequestValidator} from "./dispatch-request";
import {Message, MessageEntity, MessageValidator} from "./message";
import {PatchValue} from "./patch-value";
import {AddressPatchDTO} from "./address";

type DispatchInterface = {
  id: string,
  category: string,
  regionId: string,
  status: string,
  splitGroupId?: string
  comments?: string
  deviceId?: string
  authentication?: string
  personId?: string
  personName?: string
  personCompanyId?: string
  personCompanyName?: string
  createdById?: string
  createByPersonName?: string
  authorizeId?: string
  authorizeName?: string
  pickupContainerId?: string
  pickupContainerName?: string
  deliveryContainerId?: string
  deliveryContainerName?: string
  pickupWellId?: string
  pickupWellName?: string
  deliveryWellId?: string
  deliveryWellName?: string
  pickupSiteId?: string
  pickupSiteName?: string
  pickupSiteCompanyId?: string
  pickupSiteCompanyName?: string
  deliverySiteId?: string
  deliverySiteName?: string
  deliverySiteCompanyId?: string
  deliverySiteCompanyName?: string
  operatorId?: string
  waterTypeId?: string
  waterTypeName?: string
  pickupServiceTypeId?: string
  pickupServiceTypeName?: string
  deliveryServiceTypeId?: string
  deliveryServiceTypeName?: string
  type?: string
  vehicleId?: string
  vehicleName?: string
  trailerId?: string
  trailerName?: string
  shiftTypeId?: string
  quantityDecimal?: number
  quantityUnitOfMeasure?: string
  ticketNumber?: string
  workOrderNumber?: string
  internalWorkOrderNumber?: string
  pickupTransportId?: string
  deliveryTransportId?: string
  driverPerformStart?: boolean
  driverPerformPickup?: boolean
  driverPerformDelivery?: boolean
  performOrder?: number
  matchOverrideDttm?: number
  personAckDttm?: number
  scheduleDttm?: number
  createDttm?: number
  lastModifiedDttm?: number
  dispatchRequest?: DispatchRequest,
  message?: Message,
  splits?: Partial<Omit<DispatchInterface, "splits">>[],
  notified?: boolean,
}

const PartialDispatchValidator = t.partial({
  id: t.string,
  category: t.string,
  regionId: t.string,
  status: t.string,
  splitGroupId: t.string,
  comments: t.string,
  deviceId: t.string,
  authentication: t.string,
  personId: t.string,
  personName: t.string,
  personCompanyId: t.string,
  personCompanyName: t.string,
  createdById: t.string,
  createByPersonName: t.string,
  authorizeId: t.string,
  authorizeName: t.string,
  pickupContainerId: t.string,
  pickupContainerName: t.string,
  deliveryContainerId: t.string,
  deliveryContainerName: t.string,
  pickupWellId: t.string,
  pickupWellName: t.string,
  deliveryWellId: t.string,
  deliveryWellName: t.string,
  pickupSiteId: t.string,
  pickupSiteName: t.string,
  pickupSiteCompanyId: t.string,
  deliverySiteId: t.string,
  deliverySiteName: t.string,
  deliverySiteCompanyId: t.string,
  operatorId: t.string,
  waterTypeId: t.string,
  waterTypeName: t.string,
  pickupServiceTypeId: t.string,
  pickupServiceTypeName: t.string,
  deliveryServiceTypeId: t.string,
  deliveryServiceTypeName: t.string,
  type: t.string,
  vehicleId: t.string,
  vehicleName: t.string,
  trailerId: t.string,
  trailerName: t.string,
  trailerCompanyId: t.string,
  shiftTypeId: t.string,
  quantityDecimal: t.number,
  quantityUnitOfMeasure: t.string,
  ticketNumber: t.string,
  workOrderNumber: t.string,
  internalWorkOrderNumber: t.string,
  pickupTransportId: t.string,
  deliveryTransportId: t.string,
  driverPerformStart: t.boolean,
  driverPerformPickup: t.boolean,
  driverPerformDelivery: t.boolean,
  performOrder: t.number,
  matchOverrideDttm: t.number,
  personAckDttm: t.number,
  scheduleDttm: t.number,
  createDttm: t.number,
  lastModifiedDttm: t.number,
  dispatchRequest: DispatchRequestValidator,
  message: MessageValidator,
  notified: t.boolean,
})

export const DispatchValidator: t.Type<DispatchInterface> = t.recursion('Dispatch', () =>
  t.intersection([
    t.type({
      id: t.string,
      category: t.string,
      regionId: t.string,
      status: t.string,
    }),
    t.partial({
      splitGroupId: t.string,
      comments: t.string,
      deviceId: t.string,
      authentication: t.string,
      personId: t.string,
      personName: t.string,
      personCompanyId: t.string,
      personCompanyName: t.string,
      createdById: t.string,
      createByPersonName: t.string,
      authorizeId: t.string,
      authorizeName: t.string,
      pickupContainerId: t.string,
      pickupContainerName: t.string,
      deliveryContainerId: t.string,
      deliveryContainerName: t.string,
      pickupWellId: t.string,
      pickupWellName: t.string,
      deliveryWellId: t.string,
      deliveryWellName: t.string,
      pickupSiteId: t.string,
      pickupSiteName: t.string,
      pickupSiteCompanyId: t.string,
      pickupSiteCompanyName: t.string,
      deliverySiteId: t.string,
      deliverySiteName: t.string,
      deliverySiteCompanyId: t.string,
      deliverySiteCompanyName: t.string,
      operatorId: t.string,
      waterTypeId: t.string,
      waterTypeName: t.string,
      pickupServiceTypeId: t.string,
      pickupServiceTypeName: t.string,
      deliveryServiceTypeId: t.string,
      deliveryServiceTypeName: t.string,
      type: t.string,
      vehicleId: t.string,
      vehicleName: t.string,
      trailerId: t.string,
      trailerName: t.string,
      trailerCompanyId: t.string,
      shiftTypeId: t.string,
      quantityDecimal: t.number,
      quantityUnitOfMeasure: t.string,
      ticketNumber: t.string,
      workOrderNumber: t.string,
      internalWorkOrderNumber: t.string,
      pickupTransportId: t.string,
      deliveryTransportId: t.string,
      driverPerformStart: t.boolean,
      driverPerformPickup: t.boolean,
      driverPerformDelivery: t.boolean,
      performOrder: t.number,
      matchOverrideDttm: t.number,
      personAckDttm: t.number,
      scheduleDttm: t.number,
      createDttm: t.number,
      lastModifiedDttm: t.number,
      dispatchRequest: DispatchRequestValidator,
      message: MessageValidator,
      splits: t.array(PartialDispatchValidator),
      notified: t.boolean,
    })
  ])
)

export const DispatchArrayValidator = t.array(DispatchValidator);

export type Dispatch = t.TypeOf<typeof DispatchValidator>;
export type DispatchArray = t.TypeOf<typeof DispatchArrayValidator>;

@Exclude()
export class DispatchEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  regionId!: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toTransportCategory))
  category!: TransportCategory;

  @Expose()
  type!: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toDispatchStatus, dispatchStatus.pending))
  status!: DispatchStatus;

  @Expose()
  workOrderNumber?: string;

  @Expose()
  internalWorkOrderNumber?: string;

  @Expose()
  ticketNumber?: string;

  @Expose({name: "quantityDecimal"})
  quantity?: number;

  @Expose({name: "quantityUnitOfMeasure"})
  quantityUom?: string;

  @Expose()
  personId?: string;

  @Expose()
  personName?: string;

  @Expose()
  personCompanyId?: string;

  @Expose()
  personCompanyName?: string;

  @Expose()
  authorizeId?: string;

  @Expose()
  authorizeName?: string;

  @Expose()
  createdById?: string;

  @Expose({name: "createByPersonName"})
  createByName?: string;

  @Expose()
  pickupContainerId?: string;

  @Expose()
  pickupContainerName?: string;

  @Expose()
  deliveryContainerId?: string;

  @Expose()
  deliveryContainerName?: string;

  @Expose()
  pickupSiteId?: string;

  @Expose()
  pickupSiteName?: string;

  @Expose()
  pickupSiteCompanyId?: string;

  @Expose()
  pickupSiteCompanyName?: string;

  @Expose()
  deliverySiteId?: string;

  @Expose()
  deliverySiteName?: string;

  @Expose()
  deliverySiteCompanyId?: string;

  @Expose()
  deliverySiteCompanyName?: string;

  @Expose()
  pickupWellId?: string;

  @Expose()
  pickupWellName?: string;

  @Expose()
  deliveryWellId?: string;

  @Expose()
  deliveryWellName?: string;

  @Expose({name: "waterTypeId"})
  freightTypeId?: string;

  @Expose({name: "waterTypeName"})
  freightTypeName?: string;

  @Expose()
  pickupServiceTypeId?: string;

  @Expose()
  pickupServiceTypeName?: string;

  @Expose()
  deliveryServiceTypeId?: string;

  @Expose()
  deliveryServiceTypeName?: string;

  @Expose()
  vehicleId?: string;

  @Expose()
  vehicleName?: string;

  @Expose()
  trailerId?: string;

  @Expose()
  trailerName?: string;

  @Expose()
  trailerCompanyId?: string;

  @Expose()
  pickupTransportId?: string

  @Expose()
  deliveryTransportId?: string

  @Expose()
  driverPerformStart?: boolean

  @Expose()
  driverPerformPickup?: boolean

  @Expose()
  driverPerformDelivery?: boolean

  @Expose()
  performOrder?: number

  @Expose()
  matchOverrideDttm?: number

  @Expose()
  personAckDttm?: number

  @Expose()
  scheduleDttm?: number;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  deviceId?: string;

  @Expose()
  splitGroupId?: string;

  @Expose()
  @Type(() => DispatchRequestEntity)
  dispatchRequest?: DispatchRequestEntity

  @Expose()
  @Type(() => MessageEntity)
  message?: MessageEntity

  @Expose()
  notified?: boolean

  readonly entityType = "DISPATCH";

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for DispatchEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(dispatch: Dispatch): DispatchEntity {
    return plainToInstance(DispatchEntity, dispatch, {
      excludeExtraneousValues: true
    });
  }

  loadTypeValue(): Promise<Dispatch> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(DispatchValidator, object);
  }

  asTypeValue(): Dispatch {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(DispatchValidator, object);
  }

  get displayQuantity(): string | undefined {
    if (this.quantity && this.quantityUom) {
      return this.quantity + " " + this.quantityUom;
    } else if (this.quantity) {
      return this.quantity.toString();
    }
    return undefined;
  }

  get details() : string {
    let details = "Unknown Load Details"

    const qtyDetails = this.displayQuantity
    if (qtyDetails) {
      details = qtyDetails

      if (this.freightTypeId) {
        details += " of "
        details += (this.freightTypeName ?? "Unknown")
      }

      if (this.pickupServiceTypeId) {
        details += " ("
        details += (this.pickupServiceTypeName ?? "Service")
        details += ")"
      }
    }

    return details
  }
}

export interface DispatchSearchCriteria {
  fromDttm?: number;
  toDttm?: number;
  regionId?: string;
  personIds?: string[];
}

export interface DispatchPatchDTO {
  performOrder?: PatchValue<number>
  personId?: PatchValue<string>
  scheduleDttm?: PatchValue<number>
}
